import React, { useState, useRef, useEffect } from "react";
import "./ExportClientModal.css";
import * as XLSX from "xlsx";
import CancelBtn from "../CustomButtons/CancelBtn/CancelBtn";
// import { DateFormatter } from "../CommonUI/DateFormatter";
import { formatForInput } from "../../Utils/datepickerFormat";
interface ExportClientModalProps {
  open: boolean;
  onClose: () => void;
  dataSource: any[];
  selectedRows: number[];
}

const ExportClientModal = ({
  open,
  onClose,
  dataSource,
  selectedRows,
}: ExportClientModalProps) => {
  const [selectedFields, setSelectedFields] = useState<string[]>([
    "Name",
    "Email",
  ]);
  // const [invoices, setInvoices] = useState<any[]>([]);
  const [closeButon, setCloseButton] = useState(false);
  const [loading, setLoading] = useState(false);
  const selectAllRef = useRef<HTMLInputElement>(null);

  const fields = [
    "Name",
    "Email",
    "Items",
    "Inbox",
    "Failed Processing",
    "Pending",
    "To Review",
    "Ready",
    "Exported",
    "Archived",
    "Latest Item",
    "Oldest Item",
    "Status",
  ];

  const allOptionalFields = fields.filter(
    (field) => field !== "Name" && field !== "Email",
  );

  const isAllSelected = allOptionalFields.every((field) =>
    selectedFields.includes(field),
  );

  const isIndeterminate =
    !isAllSelected &&
    allOptionalFields.some((field) => selectedFields.includes(field));

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  if (!open) return null;

  const handleSelectAll = () => {
    if (isAllSelected) {
      // Keep mandatory fields only
      setSelectedFields(["Name", "Email"]);
    } else {
      // Select everything
      setSelectedFields(fields);
    }
  };

  const handleCheckboxChange = (field: string) => {
    // Prevent removing mandatory fields
    if (field === "Name" || field === "Email") return;

    setSelectedFields((prev) =>
      prev.includes(field)
        ? prev.filter((item) => item !== field)
        : [...prev, field],
    );
  };

  const getCircleStatusText = (value: number | string) => {
    const normalized = typeof value === "string" ? value.toLowerCase() : value;

    switch (normalized) {
      case 0:
      case "pending":
        return { text: "Pending", color: "#ef6713ff" };

      case 1:
      case "active":
        return { text: "Active", color: "green" };

      case 2:
      case "deactive":
        return { text: "Deactive", color: "red" };

      case 3:
      case "expired":
        return { text: "Expired", color: "orange" };

      default:
        return { text: "Unknown", color: "gray" };
    }
  };

  const formatDate = (value: any) => {
    if (!value) return "";

    const date = new Date(value);

    if (isNaN(date.getTime())) return value;

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const exporttoexcel = async () => {
    const tableWrapper = document.querySelector(
      ".table-wrapper",
    ) as HTMLElement;

    const savedScrollTop = tableWrapper ? tableWrapper.scrollTop : 0;

    setLoading(true);

    try {
      const selectedClients = dataSource.filter((client: any) =>
        selectedRows.includes(client.id),
      );

      // map UI labels → actual object keys
      const fieldKeyMap: Record<string, string> = {
        Name: "name",
        Email: "email",
        Items: "items",
        Inbox: "inbox",
        "Failed Processing": "failedProcessed",
        Pending: "pending",
        "To Review": "to_Review",
        Ready: "ready",
        Exported: "exported",
        Archived: "archived",
        "Latest Item": "latestItem",
        "Oldest Item": "oldestItem",
        Status: "status",
      };

      // Keep export columns in the same order as the fields array
      const orderedSelectedFields = fields.filter((field) =>
        selectedFields.includes(field),
      );

      const keys = orderedSelectedFields
        .map((field) => fieldKeyMap[field])
        .filter(Boolean);

      const headerLabels = orderedSelectedFields;

      const dateFields = ["latestItem", "oldestItem"];

      const rowData = selectedClients.map((client: any) =>
        keys.map((key) => {
          const value = client?.[key];

          if (dateFields.includes(key)) {
            return formatForInput(value);
          }

          if (key === "status") {
            return getCircleStatusText(value).text;
          }

          return value ?? "";
        }),
      );

      const worksheetData = [headerLabels, ...rowData];

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "Selected Clients");

      const today = new Date();
      const formattedDate = `${today.getDate()}-${
        today.getMonth() + 1
      }-${today.getFullYear()}`;

      XLSX.writeFile(workbook, `clients-${formattedDate}.xlsx`);
    } catch (error) {
      console.error("Export error:", error);
    } finally {
      setLoading(false);

      setTimeout(() => {
        const wrapper = document.querySelector(".table-wrapper") as HTMLElement;

        if (wrapper) {
          wrapper.scrollTop = savedScrollTop;
        }
      }, 300);
    }
  };
  const chunkedFields = [];

  for (let i = 0; i < fields.length; i += 2) {
    chunkedFields.push(fields.slice(i, i + 2));
  }
  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Export client data as Excel file</h3>
          <button className="close-btn" onClick={onClose}>
            <CancelBtn closeButon={closeButon} />
          </button>
        </div>

        <div className="modal-body">
          <p>
            ℹ️ You will be exporting {selectedRows.length} client
            {selectedRows.length !== 1 ? "s" : ""}
          </p>
          <div
            style={{
              background: "#f2f2f2",
              padding: "10px",
              borderRadius: "5px",
              marginBottom: "15px",
            }}
          >
            <h4>How exporting works</h4>

            <ol>
              <li>Select what you want to export</li>
              <li>Click export to generate your Excel file</li>
              <li>Download your Excel file</li>
            </ol>
          </div>

          {/* Select All Checkbox */}
          <div style={{ marginBottom: "12px" }}>
            <label className="checkbox-item">
              <input
                ref={selectAllRef}
                type="checkbox"
                checked={isAllSelected}
                onChange={handleSelectAll}
                style={{ accentColor: "var(--color-brand)", width: "16px", height: "16px" }}
              />
              <span style={{ fontWeight: "bold" }}>Select what to export</span>
            </label>
          </div>

          <div className="checkbox-container">
            {chunkedFields.map((pair, index) => (
              <div key={index} className="checkbox-row">
                {pair.map((field) => {
                  const isMandatory = field === "Name" || field === "Email";

                  return (
                    <label key={field} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={selectedFields.includes(field)}
                        disabled={isMandatory}
                        onChange={() => handleCheckboxChange(field)}
                      />
                      {field}
                    </label>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose}>
            Cancel
          </button>

          <button className="export-btn" onClick={exporttoexcel}>
            Export data
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportClientModal;
